import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { MessageCircle, MapPin, Instagram, Facebook, LifeBuoy, Mail } from "lucide-react";
import { buildWhatsAppUrl } from "@/lib/format";
import { cachedGet } from "@/lib/api";
import { useCategoryTree } from "./Header";
import { useSettings } from "@/context/SettingsContext";

// Rodapé em 4 grupos: HG Móveis · Catálogos · Informações · Atendimento
export const Footer = () => {
  const categories = useCategoryTree();
  const settings = useSettings();
  const [pages, setPages] = useState([]);
  useEffect(() => {
    cachedGet("/pages", 300000).then((data) => setPages(data)).catch(() => {});
  }, []);

  // links de catálogo: configurados no painel ou os 4 principais + "ver todos" (padrão do lojista)
  const MAIN_CATALOG_SLUGS = ["guarda-roupas", "sofas", "camas", "cozinha"];
  const catalogLinks = (settings.catalog_links || []).length > 0
    ? settings.catalog_links
    : [
        ...MAIN_CATALOG_SLUGS
          .map((s) => categories.find((c) => c.slug === s))
          .filter(Boolean)
          .map((c) => ({ label: c.name, slug: c.slug })),
        { label: "Ver todos os catálogos", slug: "" },
      ];
  const ast = settings.assistencia || {};

  return (
    <footer className="bg-[#07335C] text-slate-300 mt-16" data-testid="store-footer">
      <div className="container-store py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* HG Móveis */}
        <div>
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="HG Móveis" className="h-12 w-12" loading="lazy" decoding="async" />
            <span className="font-heading font-bold text-xl text-white">HG Móveis</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">{settings.footer_about}</p>
          <div className="mt-4 flex items-start gap-2 text-sm text-slate-400">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{settings.footer_location}</span>
          </div>
          {(settings.instagram_url || settings.facebook_url) && (
            <div className="mt-5 flex gap-3" data-testid="footer-social">
              {settings.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram da HG Móveis"
                  className="p-2.5 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 text-white hover:scale-110 transition-transform"
                  data-testid="footer-instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {settings.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook da HG Móveis"
                  className="p-2.5 rounded-full bg-[#1877F2] text-white hover:scale-110 transition-transform"
                  data-testid="footer-facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
            </div>
          )}
        </div>

        {/* Catálogos */}
        <div>
          <h4 className="font-heading font-semibold text-white mb-4">Catálogos</h4>
          <ul className="space-y-2 text-sm">
            {catalogLinks.map((l, i) => (
              <li key={`${l.slug}-${i}`}>
                <Link
                  to={l.slug ? `/catalogo?categoria=${l.slug}` : "/catalogo"}
                  className="hover:text-white transition-colors"
                  data-testid={`footer-catalog-${l.slug || "todos"}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Informações */}
        <div>
          <h4 className="font-heading font-semibold text-white mb-4">Informações</h4>
          <ul className="space-y-2 text-sm text-slate-400">
            <li>
              <Link to="/produtos" className="hover:text-white transition-colors" data-testid="footer-link-all">
                Todos os Produtos
              </Link>
            </li>
            <li>
              <Link to="/acompanhar" className="hover:text-white transition-colors" data-testid="footer-link-track">
                Acompanhar meu pedido
              </Link>
            </li>
            {pages.map((p) => (
              <li key={p.slug}>
                <Link to={`/pagina/${p.slug}`} className="hover:text-white transition-colors" data-testid={`footer-page-${p.slug}`}>
                  {p.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Atendimento */}
        <div>
          <h4 className="font-heading font-semibold text-white mb-4">Atendimento</h4>
          <ul className="space-y-3 text-sm text-slate-400">
            <li>
              <a
                data-testid="footer-whatsapp-button"
                href={buildWhatsAppUrl("Olá! Vim pelo site da HG Móveis e quero atendimento.", settings.whatsapp_number)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-semibold rounded-full px-4 py-2 text-sm transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Vendas: {settings.whatsapp_display}
              </a>
            </li>
            <li>
              <a
                href={buildWhatsAppUrl("Olá! Preciso de assistência técnica da HG Móveis.", (ast.whatsapp || "5521997459912").replace(/\D/g, ""))}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 hover:text-white transition-colors"
                data-testid="footer-link-assistencia"
              >
                <LifeBuoy className="w-4 h-4 text-blue-300" /> Assistência Técnica: (21) 99745-9912
              </a>
            </li>
            <li>
              <a
                href={`mailto:${ast.email || "hgmoveis2022@gmail.com"}`}
                className="flex items-center gap-2 hover:text-white transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4 text-blue-300" /> {ast.email || "hgmoveis2022@gmail.com"}
              </a>
            </li>
          </ul>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">
            Atendimento de segunda a sábado, em horário comercial.
            Pagamento na entrega: PIX, dinheiro ou cartão em até 12x.
          </p>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-store py-5 flex flex-col items-center gap-1.5 text-xs text-slate-400 text-center">
          <p>*Entrega em até 24 horas sob disponibilidade da região e confirmação da equipe.</p>
          <p data-testid="footer-demo-disclaimer">
            Alguns produtos exibidos são demonstrações do catálogo e serão substituídos.
          </p>
          <p>© {new Date().getFullYear()} HG Móveis — Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
};
